import type { ReactNode } from 'react';
import { cx } from './cx';

export interface C1Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /**
   * 'end' right-aligns the column and puts it in tabular figures, so a column of
   * numbers lines up on its digits. Use it for every quantity; leave text at the
   * default, because right-aligned words are hard to scan.
   */
  align?: 'start' | 'end';
  /**
   * Marks the column that says which row this is — a name, a reference. Rendered
   * as `<th scope="row">`, so a screen reader announces "Fee payment recorded,
   * amount, 12 May" instead of reading three bare cells with no subject.
   *
   * At most one column should set it.
   */
  rowHeader?: boolean;
  /** A CSS width for the column, e.g. '30%' or '120px'. */
  width?: string;
}

interface C1TableProps<Row> {
  /**
   * What the table is, as a sentence. Required, and visually hidden unless
   * `captionVisible` — a table announced only as "table with 5 columns" is a table
   * nobody arriving by keyboard can identify.
   */
  caption: ReactNode;
  captionVisible?: boolean;
  columns: Array<C1Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Shown in place of the body when there are no rows. */
  empty?: ReactNode;
  /** Caps the height and scrolls, for a long list inside a dashboard panel. */
  scroll?: boolean;
  className?: string;
}

/**
 * A data table.
 *
 * NO WHOLE-ROW LINK, deliberately. An anchor cannot wrap a `<tr>`, and the usual
 * workaround — an onClick on the row — produces something that looks like a link,
 * cannot be opened in a new tab, does not appear in the tab order and says nothing
 * to a screen reader. Put a `C1Link` in the row-header cell instead: it is a real
 * link, it is reachable, and the row still reads as one record.
 */
export function C1Table<Row>({
  caption,
  captionVisible = false,
  columns,
  rows,
  rowKey,
  empty,
  scroll = false,
  className,
}: C1TableProps<Row>) {
  const body =
    rows.length === 0 ? (
      <tr>
        <td className="c1-tableEmpty" colSpan={columns.length}>
          {empty}
        </td>
      </tr>
    ) : (
      rows.map((row) => (
        <tr key={rowKey(row)}>
          {columns.map((column) => {
            const cellClass = cx(column.align === 'end' && 'c1-cell--end');
            return column.rowHeader ? (
              <th key={column.key} scope="row" className={cellClass}>
                {column.cell(row)}
              </th>
            ) : (
              <td key={column.key} className={cellClass}>
                {column.cell(row)}
              </td>
            );
          })}
        </tr>
      ))
    );

  return (
    <div className={cx('c1-tableWrap', scroll && 'c1-tableWrap--scroll', className)}>
      <table className="c1-table">
        <caption className={captionVisible ? 'c1-tableCaption' : 'sr-only'}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cx(column.align === 'end' && 'c1-cell--end')}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
